import { useEffect, useRef } from "react";
import { platforms, Product } from "@/data/products";
import { useCart } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";

// Extend Navigator for WebMCP
declare global {
  interface Navigator {
    modelContext?: {
      registerTool: (tool: {
        name: string;
        description: string;
        inputSchema: Record<string, unknown>;
        execute: (input: Record<string, unknown>) => Promise<unknown>;
        annotations?: Record<string, unknown>;
      }) => void;
    };
  }
}

export function useWebMCP() {
  const { addItem, removeItem, clearCart, items, totalItems, totalPrice } = useCart();
  const navigate = useNavigate();
  const registered = useRef(false);

  useEffect(() => {
    if (!navigator.modelContext || registered.current) return;
    registered.current = true;
    const mc = navigator.modelContext;

    // 1. Search products
    mc.registerTool({
      name: "search_products",
      description: "Search for social media marketing products/services by keyword. Returns matching products with name, price, platform, and link.",
      inputSchema: {
        type: "object",
        properties: {
          query: { type: "string", description: "Search keyword (e.g. 'instagram followers', 'youtube views')" },
          limit: { type: "number", description: "Max results to return (default 10)" },
        },
        required: ["query"],
      },
      annotations: { readOnlyHint: true },
      execute: async (input) => {
        const q = (input.query as string).toLowerCase();
        const limit = (input.limit as number) || 10;
        const results: { name: string; price: string; platform: string; url: string; minQuantity: number }[] = [];
        for (const platform of platforms) {
          for (const product of platform.products) {
            if (
              product.name.toLowerCase().includes(q) ||
              product.label.toLowerCase().includes(q) ||
              platform.name.toLowerCase().includes(q)
            ) {
              results.push({
                name: product.name,
                price: product.price,
                platform: platform.name,
                url: `/${platform.slug}/${product.slug}`,
                minQuantity: product.minQuantity,
              });
              if (results.length >= limit) break;
            }
          }
          if (results.length >= limit) break;
        }
        return { results, total: results.length };
      },
    });

    // 2. List platforms
    mc.registerTool({
      name: "list_platforms",
      description: "List all available social media platforms with their categories and product counts.",
      inputSchema: { type: "object", properties: {} },
      annotations: { readOnlyHint: true },
      execute: async () => {
        return platforms.map((p) => ({
          name: p.name,
          slug: p.slug,
          url: `/${p.slug}`,
          categories: p.categories.map((c) => c.name),
          productCount: p.products.length,
        }));
      },
    });

    // 3. Get platform details
    mc.registerTool({
      name: "get_platform",
      description: "Get details about a specific social media platform including all its categories and products.",
      inputSchema: {
        type: "object",
        properties: {
          platform: { type: "string", description: "Platform slug (e.g. 'instagram', 'youtube', 'tiktok')" },
        },
        required: ["platform"],
      },
      annotations: { readOnlyHint: true },
      execute: async (input) => {
        const p = platforms.find((pl) => pl.slug === input.platform);
        if (!p) return { error: "Platform not found" };
        return {
          name: p.name,
          categories: p.categories.map((c) => ({ name: c.name, slug: c.slug })),
          products: p.products.map((pr) => ({
            id: pr.id,
            name: pr.name,
            price: pr.price,
            slug: pr.slug,
            url: `/${p.slug}/${pr.slug}`,
          })),
        };
      },
    });

    // 4. Add to cart
    mc.registerTool({
      name: "add_to_cart",
      description: "Add a product to the shopping cart by product ID and quantity.",
      inputSchema: {
        type: "object",
        properties: {
          productId: { type: "string", description: "The product ID (e.g. 'in-1', 'yo-5')" },
          quantity: { type: "number", description: "Quantity to add (default: minimum quantity)" },
        },
        required: ["productId"],
      },
      annotations: { readOnlyHint: false },
      execute: async (input) => {
        let found: Product | null = null;
        for (const p of platforms) {
          const product = p.products.find((pr) => pr.id === input.productId);
          if (product) {
            found = product;
            break;
          }
        }
        if (!found) return { error: "Product not found" };
        const qty = (input.quantity as number) || found.minQuantity;
        addItem(found, qty);
        return { success: true, product: found.name, quantity: qty, price: found.price };
      },
    });

    // 5. Get cart
    mc.registerTool({
      name: "get_cart",
      description: "Get current shopping cart contents, total items, and total price.",
      inputSchema: { type: "object", properties: {} },
      annotations: { readOnlyHint: true },
      execute: async () => {
        return {
          items: items.map((i) => ({
            id: i.product.id,
            name: i.product.name,
            quantity: i.quantity,
            price: i.product.price,
          })),
          totalItems,
          totalPrice: `₪${totalPrice.toFixed(2)}`,
        };
      },
    });

    // 6. Remove from cart
    mc.registerTool({
      name: "remove_from_cart",
      description: "Remove a product from the shopping cart by product ID.",
      inputSchema: {
        type: "object",
        properties: {
          productId: { type: "string", description: "The product ID to remove" },
        },
        required: ["productId"],
      },
      annotations: { readOnlyHint: false },
      execute: async (input) => {
        removeItem(input.productId as string);
        return { success: true };
      },
    });

    // 7. Clear cart
    mc.registerTool({
      name: "clear_cart",
      description: "Remove all items from the shopping cart.",
      inputSchema: { type: "object", properties: {} },
      annotations: { readOnlyHint: false },
      execute: async () => {
        clearCart();
        return { success: true };
      },
    });

    // 8. Navigate
    mc.registerTool({
      name: "navigate_to",
      description: "Navigate to a specific page on the site (platform page, product page, checkout, etc.).",
      inputSchema: {
        type: "object",
        properties: {
          path: { type: "string", description: "URL path (e.g. '/instagram', '/checkout', '/instagram/for-1-dollar')" },
        },
        required: ["path"],
      },
      annotations: { readOnlyHint: false },
      execute: async (input) => {
        navigate(input.path as string);
        return { success: true, navigatedTo: input.path };
      },
    });

    // 9. Get product details
    mc.registerTool({
      name: "get_product",
      description: "Get detailed information about a specific product by ID.",
      inputSchema: {
        type: "object",
        properties: {
          productId: { type: "string", description: "The product ID (e.g. 'in-1')" },
        },
        required: ["productId"],
      },
      annotations: { readOnlyHint: true },
      execute: async (input) => {
        for (const p of platforms) {
          const product = p.products.find((pr) => pr.id === input.productId);
          if (product) {
            return {
              ...product,
              platform: p.name,
              platformSlug: p.slug,
              url: `/${p.slug}/${product.slug}`,
            };
          }
        }
        return { error: "Product not found" };
      },
    });
  }, [addItem, removeItem, clearCart, items, totalItems, totalPrice, navigate]);
}
